import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../utils/errorHandler';
import { checkUploadInProgress } from '../../utils/cvUploadLock';
import { mapLevelValues } from '../../utils/levelMapper';
import {
  buildGapSavePayload,
  ExplorerOptInGap,
  MappedExplorerPrefs,
} from '../../utils/profilePrefs';

interface PassportGapFormProps {
  gaps: ExplorerOptInGap[];
  initial: MappedExplorerPrefs;
  userId: string;
  apiCall: (url: string, options?: RequestInit) => Promise<Response>;
  onSaved: () => void;
  onLater: () => void;
  onOpenPassport?: () => void;
  laterLabel?: string;
}

const PassportGapForm: React.FC<PassportGapFormProps> = ({
  gaps,
  initial,
  userId,
  apiCall,
  onSaved,
  onLater,
  onOpenPassport,
  laterLabel,
}) => {
  const { t, i18n } = useTranslation(['profile', 'passport', 'inspire']);
  const currentLanguage = i18n.language?.startsWith('de') ? 'de' : 'en';
  const [form, setForm] = useState<MappedExplorerPrefs>(initial);
  const [saving, setSaving] = useState(false);

  const careerStatusOptions = useMemo(() => [
    { value: 'actively_seeking', label: t('profile:careerStatus.options.actively_seeking') },
    { value: 'curious', label: t('profile:careerStatus.options.curious') },
    { value: 'invisible', label: t('profile:careerStatus.options.invisible') },
  ], [t]);

  // Current level: same multi-select dropdown as the Passport prefs page, so
  // the nudge stays consistent (no free-text). Values are stored comma-joined.
  const levelOptions = useMemo(() => [
    t('inspire:levels.highSchool'), t('inspire:levels.apprentice'), t('inspire:levels.bachelor'),
    t('inspire:levels.master'), t('inspire:levels.workingStudent'), t('inspire:levels.phd'),
    t('inspire:levels.qualifiedWorker'), t('inspire:levels.entrepreneur'), t('inspire:levels.junior'),
    t('inspire:levels.midLevel'), t('inspire:levels.senior'), t('inspire:levels.masterCraftsman'),
    t('inspire:levels.lead'), t('inspire:levels.expert'),
  ], [t]);

  const [levelOpen, setLevelOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>(() =>
    mapLevelValues(
      (initial.current_level || '').split(',').map(l => l.trim()).filter(Boolean),
      currentLanguage,
    ),
  );
  const levelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (levelRef.current && !levelRef.current.contains(e.target as Node)) setLevelOpen(false);
    };
    if (levelOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [levelOpen]);

  useEffect(() => {
    setForm(prev => ({ ...prev, current_level: selectedLevels.join(', ') }));
  }, [selectedLevels]);

  const inputClass =
    'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500';

  const save = async () => {
    const minutesLeft = checkUploadInProgress();
    if (minutesLeft > 0) {
      showToast(t('profile:cv.uploadWait', { minutes: minutesLeft }), 'info');
      return;
    }
    const payload = buildGapSavePayload(gaps, form);
    if (Object.keys(payload).length === 0) return;

    try {
      setSaving(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const res = await apiCall(`${apiBase}/api/v1/profiles/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(60000),
      });
      if (res.ok) {
        showToast(t('passport:prefs.savedToast'), 'success');
        onSaved();
      } else {
        showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
      }
    } catch {
      showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-0.5">
      {gaps.includes('contactable') && (
        <div className="bg-pink-50/50 p-3 rounded-lg border border-pink-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_contactable}
              onChange={(e) => setForm({ ...form, is_contactable: e.target.checked })}
              className="w-4 h-4 mt-0.5 text-pink-600 border-slate-300 rounded focus:ring-pink-500"
            />
            <span>
              <span className="text-sm font-medium text-slate-800">{t('profile:contactInfo.allowContact')}</span>
              <span className="block text-xs text-slate-600 mt-0.5">{t('profile:contactInfo.allowContactHint')}</span>
            </span>
          </label>
        </div>
      )}

      {gaps.includes('recruiter_visibility') && (
        <div>
          <p className="text-xs text-slate-500 mb-1.5">{t('passport:prefs.visibilityLabel')}</p>
          <div className="space-y-1.5">
            {careerStatusOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                  form.career_status === option.value
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="gap_career_status"
                  value={option.value}
                  checked={form.career_status === option.value}
                  onChange={(e) => setForm({ ...form, career_status: e.target.value })}
                  className="sr-only"
                />
                <span className="flex-1 text-sm text-slate-700">{option.label}</span>
                {form.career_status === option.value && <Check className="w-4 h-4 text-pink-500 flex-shrink-0" />}
              </label>
            ))}
          </div>
        </div>
      )}

      {gaps.includes('base_locations') && (
        <Field label={t('profile:jobPreferences.baseLocations')}>
          <input
            type="text"
            value={form.base_locations}
            onChange={(e) => setForm({ ...form, base_locations: e.target.value })}
            className={inputClass}
          />
        </Field>
      )}

      {gaps.includes('preferred_locations') && (
        <Field label={t('profile:jobPreferences.preferredLocations')}>
          <input
            type="text"
            value={form.preferred_locations}
            onChange={(e) => setForm({ ...form, preferred_locations: e.target.value })}
            className={inputClass}
          />
        </Field>
      )}

      {gaps.includes('role_preferences') && (
        <Field label={t('profile:jobPreferences.rolePreferences')}>
          <input
            type="text"
            value={form.role_preferences}
            onChange={(e) => setForm({ ...form, role_preferences: e.target.value })}
            className={inputClass}
          />
        </Field>
      )}

      {gaps.includes('current_role') && (
        <Field label={t('profile:jobPreferences.currentRole')}>
          <input
            type="text"
            value={form.current_role}
            onChange={(e) => setForm({ ...form, current_role: e.target.value })}
            maxLength={200}
            className={inputClass}
          />
        </Field>
      )}

      {gaps.includes('current_level') && (
        <Field label={t('profile:jobPreferences.currentLevel')}>
          <div className={`relative ${levelOpen ? 'z-30' : ''}`} ref={levelRef}>
            <button
              type="button"
              onClick={() => setLevelOpen(!levelOpen)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white text-left pr-10"
            >
              <span className={selectedLevels.length === 0 ? 'text-slate-400' : 'text-slate-700'}>
                {selectedLevels.length === 0
                  ? '\u00a0'
                  : selectedLevels.length === 1
                  ? selectedLevels[0]
                  : t('profile:jobPreferences.levelsSelected', { count: selectedLevels.length })}
              </span>
              {levelOpen
                ? <ChevronUp className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
            </button>
            {levelOpen && (
              <div className="absolute z-30 bottom-full mb-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {levelOptions.map(option => (
                  <label key={option} className="flex items-center px-3 py-2 hover:bg-pink-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLevels.includes(option)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedLevels([...selectedLevels, option]);
                        else setSelectedLevels(selectedLevels.filter(l => l !== option));
                      }}
                      className="mr-2 h-4 w-4 text-pink-600 focus:ring-pink-400 border-slate-300 rounded"
                    />
                    <span className="text-sm text-slate-700">{option}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </Field>
      )}

      <div className="flex gap-2 pt-1 sticky bottom-0 bg-white">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex-1 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 transition-all"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('passport:common.save')}
        </button>
        <button
          type="button"
          onClick={onLater}
          disabled={saving}
          className="px-3 py-2 rounded-xl text-sm text-slate-500 hover:bg-slate-50 transition-colors"
        >
          {laterLabel || t('passport:common.cancel')}
        </button>
      </div>

      {onOpenPassport && (
        <button
          type="button"
          onClick={onOpenPassport}
          className="w-full text-center text-[11px] text-pink-600 hover:text-pink-700 underline underline-offset-2"
        >
          {t('profile:passportLink', 'Open in Passport')}
        </button>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-xs text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);

export default PassportGapForm;
